// @flow
import * as jsts from "jsts";
import geolib from "geolib";
import type {PositionAsDecimal} from "geolib";
import debounce from "lodash/debounce";

export type CoordinateParam = number;

export type LatLngLiteral = {
    lat: CoordinateParam,
    lng: CoordinateParam
};

export const getLatLngLiteralFromLatLng = function (latlng: window.google.maps.LatLng): LatLngLiteral {
    return {
        lat: parseFloat(latlng.lat()),
        lng: parseFloat(latlng.lng())
    };
};

export const getLatLngFromLatLngLiteral = function (latlng: LatLngLiteral): window.google.maps.LatLng {
    if (typeof latlng.lat === "function") {
        return latlng;
    }
    return new window.google.maps.LatLng(latlng);
};

export const areLatLngsEqual = function (latlng1: LatLngLiteral, latlng2: LatLngLiteral): boolean {
    try {
        return (latlng1.lat === latlng2.lat && latlng1.lng === latlng2.lng);
    } catch (e) {
        return false;
    }
};

export const getPositionAsDecimalFromLatLngLiteral = (latlng: LatLngLiteral): PositionAsDecimal => {
    return {latitude: parseFloat(latlng.lat), longitude: parseFloat(latlng.lng)};
};

export const getPositionAsDecimalFromLatLng = (latlng: window.google.maps.LatLng): PositionAsDecimal => {
    return {latitude: parseFloat(latlng.lat()), longitude: parseFloat(latlng.lng())};
};

export const getLatLngFromPositionAsDecimal = (latlng: PositionAsDecimal) => {
    return new window.google.maps.LatLng({lat: latlng.latitude, lng: latlng.longitude});
};

export const getDistance = (latlng1: PositionAsDecimal, latlng2: PositionAsDecimal, accuracy: number = 5) =>
    geolib.getDistance(latlng1, latlng2);
export const getBearing = (origin: PositionAsDecimal, dest: PositionAsDecimal) =>
    geolib.getBearing(origin, dest);
export const computeDestinationPoint = (start: PositionAsDecimal, distance: number, bearing: number) =>
    geolib.computeDestinationPoint(start, distance, bearing);


const polygonArea = (polygonPath: Array<LatLngLiteral>): number => {
    var area = 0;
    for (var i = 0; i < polygonPath.length; i++) {
        var j = (i + 1) % polygonPath.length;
        area += polygonPath[i]["lat"] * polygonPath[j]["lng"];
        area -= polygonPath[j]["lat"] * polygonPath[i]["lng"];
    }
    return area / 2;
};

const isPolygonClockwise = (polygonPath: Array<LatLngLiteral>): boolean => {
    return polygonArea(polygonPath) > 0;
};

export const makePolygonPathClockwise = (polygonPath: Array<LatLngLiteral>): Array<LatLngLiteral> => {
    if (!isPolygonClockwise(polygonPath)) {
        polygonPath.reverse();
    }
    return polygonPath;
};

export const createJstsPolygon = (polygon: window.google.maps.Polygon): jsts.geom.Polygon => {
    const geometryFactory = new jsts.geom.GeometryFactory();
    const path = polygon.getPath();
    const coordinates = path.getArray().map(function name(coord) {
        return new jsts.geom.Coordinate(coord.lat(), coord.lng());
    });
    if (coordinates[0].compareTo(coordinates[coordinates.length - 1]) !== 0)
        coordinates.push(coordinates[0]);
    const shell = geometryFactory.createLinearRing(coordinates);
    return geometryFactory.createPolygon(shell);
};


export const isLatLngInPolygon = (latlng: LatLngLiteral, latlngArray: Array<LatLngLiteral>) => {
    return geolib.isPointInside(latlng, latlngArray);
};

export const isLatLngInListOfPolygons = (latlng: LatLngLiteral, latlngArrayList: Array<Array<LatLngLiteral>>) => {
    return latlngArrayList.some((latlngArray) => isLatLngInPolygon(latlng, latlngArray));
};

export const getPolygonIfLatLngInPolygon = (latlng: LatLngLiteral, polygonList: Array<window.google.maps.Polygon>) => {
    for (let polygon of polygonList) {
        if (isLatLngInPolygon(latlng, polygon.bounding_latlngs)) {
            return polygon;
        }
    }
};

export const areTwoLatLngsInSamePolygon = (latlng1: LatLngLiteral, latlng2: LatLngLiteral, polygonList: Array<window.google.maps.Polygon>) => {
    let polygon1 = getPolygonIfLatLngInPolygon(latlng1, polygonList);
    if (!polygon1) {
        return false;
    }
    return isLatLngInPolygon(latlng2, polygon1.bounding_latlngs);
};

export const getPolygonIfLatLngInManualSelectionPolygon = (latlng: LatLngLiteral, polygonList: Array<window.google.maps.Polygon>) => {
    for (let i = 0; i < polygonList.length; i += 1) {
        let currentPolygon = polygonList[i];
        if (currentPolygon.manual_whitelist_selection) {
            if (isLatLngInPolygon(latlng, currentPolygon.bounding_latlngs)) {
                return currentPolygon;
            }
        }
    }
    return null;
};

export const debouncedLatLngGeocoder = (successHandler: (address: string) => void) => {
    return debounce((latlng) => {
        return getAddressFromLatLng(latlng, successHandler);
    }, 300);
};

const getAddressFromLatLng = (latlng: window.google.map.LatLng, successHandler: (address: string) => void) => {
    let geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({"latLng": latlng}, function (results, status) {
        if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            return successHandler(results[0].formatted_address);
        }
        return successHandler("");
    });
};

/**
 * Cubic Bezier
 * source: http://jsfiddle.net/tpud52ow/
 * @param latlong1
 * @param latlong2
 * @param latlong3
 * @param latlong4
 * @param resolution
 * @returns {*}
 * @constructor
 */
export function CubicBezierPath(
    latlong1: window.google.maps.LatLng,
    latlong2: window.google.maps.LatLng,
    latlong3: window.google.maps.LatLng,
    latlong4: window.google.maps.LatLng,
    resolution: number) {

    let lat1 = latlong1.lat();
    let long1 = latlong1.lng();
    let lat2 = latlong2.lat();
    let long2 = latlong2.lng();
    let lat3 = latlong3.lat();
    let long3 = latlong3.lng();
    let lat4 = latlong4.lat();
    let long4 = latlong4.lng();

    let points = [];

    for (let i = 0; i <= 1; i += resolution) {
        points.push(getBezier(
            {x: lat1, y: long1},
            {x: lat2, y: long2},
            {x: lat3, y: long3},
            {x: lat4, y: long4}, i)
        );
    }
    let path = [];
    path.push(new window.google.maps.LatLng(points[0].x, points[0].y, false));
    for (let i = 1; i < points.length; i += 1) {
        path.push(new window.google.maps.LatLng(points[i].x, points[i].y));
    }
    path.push(new window.google.maps.LatLng(lat1, long1));

    return path;
}

const exp1 = (t) => t * t * t;
const exp2 = (t) => 3 * t * t * (1 - t);
const exp3 = (t) => 3 * t * (1 - t) * (1 - t);
const exp4 = (t) => (1 - t) * (1 - t) * (1 - t);
const getBezier = (C1, C2, C3, C4, percent) => {
    let pos = {};
    pos.x = C1.x * exp1(percent) + C2.x * exp2(percent) + C3.x * exp3(percent) + C4.x * exp4(percent);
    pos.y = C1.y * exp1(percent) + C2.y * exp2(percent) + C3.y * exp3(percent) + C4.y * exp4(percent);
    return pos;
};

/**
 * Draw a curved google maps polyline
 * Source: http://jsfiddle.net/tpud52ow/
 * @param {google.maps.LatLng} P1
 * @param {google.maps.LatLng} P2
 * @returns {CubicBezierPath}
 */
export const getCurvedPolylinePath = (P1: window.google.maps.LatLng, P2: window.google.maps.LatLng) => {
    const P1Position = getPositionAsDecimalFromLatLng(P1);
    const P2Position = getPositionAsDecimalFromLatLng(P2);
    const lineLength = getDistance(P1Position, P2Position);
    const lineHeading = getBearing(P1Position, P2Position);
    const lineHeading1 = lineHeading < 0 ? lineHeading + 45 : lineHeading + -45;
    const lineHeading2 = lineHeading < 0 ? lineHeading + 135 : lineHeading + -135;
    const pA = computeDestinationPoint(P1Position, lineLength / 2.2, lineHeading1);
    const pB = computeDestinationPoint(P2Position, lineLength / 2.2, lineHeading2);
    const pALatLng = getLatLngFromPositionAsDecimal(pA);
    const pBLatLng = getLatLngFromPositionAsDecimal(pB);

    return CubicBezierPath(P1, pALatLng, pBLatLng, P2, 0.01);
};


// WEBPACK FOOTER //
// ./src/Utils/MapUtils.js